import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Box,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  FormControlLabel,
  FormGroup,
  Switch,
} from "@mui/material";
import Loading from "../../../../../components/loading/loading";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getUserCertificationsRequest } from "../../../../../redux/users/slice";
import { updateBackgroundCheckRequest } from "../../../../../utils/APICalls";
import { getCertificationMethodsRequest } from "../../../../../redux/inspection-firms/slice";
import FreddieMacCertificateDetailRow from "./FreddieMacCertificateDetailRow";
import FannieMaeCertificateDetailRow from "./FannieMaeCertificateDetailRow";
import "./certifications.css";

type props = {
  data: any;
  isMyOrganisation?: boolean;
};

const Certifications = ({ data, isMyOrganisation }: props) => {
  const dispatch = useDispatch();
  const [isBackgroundCheckCompleted, setIsBackgroundCheckCompleted] =
    useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    dispatch(getUserCertificationsRequest({ id: data?.id }));
    dispatch(getCertificationMethodsRequest());
  }, [data, dispatch]);

  const handleStatusChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;
    setIsBackgroundCheckCompleted(isChecked);
    setShowLoading(true);
    setErrorMessage("");
    const response = await updateBackgroundCheckRequest(data?.id, isChecked);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getUserCertificationsRequest({ id: data?.id }));
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(
        response?.data?.detail || "Failed to update background check."
      );
      // revert toggle on failure
      setIsBackgroundCheckCompleted(!isChecked);
    }
  };

  const {
    loading,
    user: { currentUser, certifications },
  } = useSelector((state: any) => state.user);
  const { certificationMethods } = useSelector(
    (state: any) => state.inspectionFirms
  );

  useEffect(() => {
    if (certifications) {
      setIsBackgroundCheckCompleted(certifications.backgroundCheckDone);
    }
  }, [certifications]);

  return (
    <>
      <Card className="card-layout">
        <Box className="card-header">
          <Typography variant="h5">Certifications</Typography>
        </Box>
        <Box className="user-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
              Background check
              <br />
              Completed?
            </Typography>
            <Tooltip
              title="Background checks are not necessary for all jobs, but you will be limited to the jobs for which you qualify without a background check."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={isBackgroundCheckCompleted}
                  onChange={handleStatusChange}
                  disabled={
                    currentUser.userType.toString() !== "0" || showLoading
                  }
                />
              }
              label={isBackgroundCheckCompleted ? "Yes" : "No"}
            />
          </FormGroup>
        </Box>
        <Divider />
        <FannieMaeCertificateDetailRow
          certificationType="Fannie Mae"
          certificate={
            certifications?.certifications?.filter(
              (certification: any) =>
                certification.certificationType === "FannieMae"
            )[0]
          }
          inspectorFirmId={data?.id}
          certificationMethods={certificationMethods}
          isMyOrganisation={isMyOrganisation}
        />
        <Divider />
        <FreddieMacCertificateDetailRow
          certificationType="Freddie Mac"
          certificate={
            certifications?.certifications?.filter(
              (certification: any) =>
                certification.certificationType === "FreddieMac"
            )[0]
          }
          inspectorFirmId={data?.id}
          isFreddieMacExemptInspector={
            certifications?.isFreddieMacExemptInspector
          }
          certificationMethods={certificationMethods}
          isMyOrganisation={isMyOrganisation}
        />
      </Card>
      {(loading || showLoading) && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </>
  );
};

export default Certifications;
